import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Avatar,
  Chip,
  Divider,
} from "@mui/material";
import { Logout, DeleteForever } from "@mui/icons-material";
import { useAccount } from "@/hooks/useAccount";
import { PageLoading } from "@/components/common/PageLoading";
import { ConfirmDeleteDialog } from "@/components/common/ConfirmDeleteDialog";

export function AccountPage() {
  const { t } = useTranslation();
  const account = useAccount();

  if (account.loading) return <PageLoading />;

  return (
    <Box sx={{ maxWidth: 700, mx: "auto" }}>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          mb: 4,
        }}
      >
        {t("account.title")}
      </Typography>
      <ProfileCard account={account} />
      <OrganizationsCard organizations={account.profile?.organizations} />
      <SignOutButton onLogout={account.handleLogout} />
      <DangerZoneCard
        deleteOpen={account.deleteOpen}
        onOpenDelete={() => account.setDeleteOpen(true)}
        onCloseDelete={() => account.setDeleteOpen(false)}
        onDelete={account.handleDelete}
      />
    </Box>
  );
}

function ProfileCard({ account }: { account: ReturnType<typeof useAccount> }) {
  const { t } = useTranslation();
  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            mb: 2,
          }}
        >
          {t("account.profile")}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 3, mb: 3 }}>
          <Avatar
            sx={{
              width: 64,
              height: 64,
              bgcolor: "primary.main",
              fontSize: 24,
            }}
          >
            {(account.user?.username ||
              account.user?.email ||
              "?")[0].toUpperCase()}
          </Avatar>
          <Box>
            <Typography
              sx={{
                fontWeight: 600,
              }}
            >
              {account.user?.username || account.user?.email}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
              }}
            >
              {account.user?.email}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-end" }}>
          <TextField
            label={t("account.username")}
            value={account.username}
            onChange={(e) => account.setUsername(e.target.value)}
            fullWidth
            size="small"
          />
          <Button
            variant="contained"
            onClick={account.handleUpdateUsername}
            disabled={!account.username.trim()}
          >
            {t("common.save")}
          </Button>
        </Box>
        <TextField
          label={t("account.email")}
          value={account.user?.email || ""}
          fullWidth
          size="small"
          disabled
          sx={{ mt: 2 }}
        />
      </CardContent>
    </Card>
  );
}

function OrganizationsCard({ organizations }: { organizations?: any[] }) {
  const { t } = useTranslation();
  if (!organizations?.length) return null;

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            mb: 2,
          }}
        >
          {t("nav.organizations")}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {organizations.map((org: any) => (
            <Box
              key={org.id}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 1.5,
                bgcolor: "action.hover",
                borderRadius: 1,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 500,
                }}
              >
                {org.name}
              </Typography>
              <Chip
                label={t(`org.${org.role}`)}
                size="small"
                color={org.role === "owner" ? "primary" : "default"}
                variant="outlined"
              />
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

function SignOutButton({ onLogout }: { onLogout: () => void }) {
  const { t } = useTranslation();
  return (
    <Button
      variant="outlined"
      startIcon={<Logout />}
      onClick={onLogout}
      fullWidth
      sx={{ mb: 3, py: 1.5 }}
    >
      {t("auth.signOut")}
    </Button>
  );
}

function DangerZoneCard({
  deleteOpen,
  onOpenDelete,
  onCloseDelete,
  onDelete,
}: {
  deleteOpen: boolean;
  onOpenDelete: () => void;
  onCloseDelete: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      <Card variant="outlined" sx={{ borderColor: "error.main" }}>
        <CardContent>
          <Typography
            variant="h6"
            color="error"
            sx={{
              fontWeight: 600,
              mb: 1,
            }}
          >
            {t("account.dangerZone")}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 2,
            }}
          >
            {t("account.deleteConfirm")}
          </Typography>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteForever />}
            onClick={onOpenDelete}
          >
            {t("account.deleteAccount")}
          </Button>
        </CardContent>
      </Card>
      <ConfirmDeleteDialog
        open={deleteOpen}
        onClose={onCloseDelete}
        onConfirm={onDelete}
        title={t("account.deleteAccount")}
        message={t("account.deleteConfirm")}
      />
    </>
  );
}
